import {
  Box,
  Container,
  Flex,
  Stack,
  Text,
  HStack,
  Link,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowUpIcon } from "@chakra-ui/icons";
import { FaLinkedin, FaGithub } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useState, useRef, useCallback } from "react";
import t from "../translations";

const MotionBox = motion(Box);

export default function Footer({ color }) {
  const textColor = useColorModeValue("gray.500", "gray.400");
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [easterEgg, setEasterEgg] = useState(false);
  const tapCount = useRef(0);
  const tapTimer = useRef(null);

  const videoRef = useRef(null);

  const handleSecretTap = useCallback(() => {
    if (easterEgg) return;
    tapCount.current += 1;
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 1500);
    if (tapCount.current >= 7) {
      tapCount.current = 0;
      setEasterEgg(true);
    }
  }, [easterEgg]);

  const handleVideoReady = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, ease: "easeOut" }}
      bg={useColorModeValue("gray.50", "gray.900")}
    >
      <Box
        h="1px"
        bgGradient="linear(to-r, transparent, purple.400, transparent)"
      />

      <Container as={Stack} maxW="6xl" py={6}>
        <Stack
          direction={{ base: "column", md: "row" }}
          align="center"
          spacing={4}
        >
          <Flex flex="1" justify={{ base: "center", md: "flex-start" }}>
            <Text fontSize="sm" color={textColor}>
              &copy; {new Date().getFullYear()} {t.footer.copyright}
            </Text>
          </Flex>
          <Flex flex="1" justify="center" align="center" gap={3}>
            <Text fontSize="xs" color={textColor}>
              {t.footer.builtWith}
            </Text>
            <IconButton
              icon={<ArrowUpIcon />}
              aria-label="Back to top"
              size="xs"
              variant="ghost"
              colorScheme={color}
              onClick={scrollToTop}
              _hover={{ transform: "translateY(-2px)" }}
              transition="all 0.2s"
            />
          </Flex>
          <Flex flex="1" justify={{ base: "center", md: "flex-end" }}>
            <HStack spacing={4}>
              <Link
                href={t.profile.linkedin}
                isExternal
                aria-label="LinkedIn"
                color={textColor}
                _hover={{
                  color: `${color}.400`,
                  transform: "scale(1.15) translateY(-2px)",
                }}
                transition="all 0.2s"
                display="inline-flex"
              >
                <FaLinkedin size={18} />
              </Link>
              <Link
                href={t.profile.github}
                isExternal
                aria-label="GitHub"
                color={textColor}
                _hover={{
                  color: `${color}.400`,
                  transform: "scale(1.15) translateY(-2px)",
                }}
                transition="all 0.2s"
                display="inline-flex"
              >
                <FaGithub size={18} />
              </Link>
              <Box
                as="button"
                w="18px"
                h="18px"
                minW="18px"
                opacity={0}
                cursor="default"
                onClick={handleSecretTap}
                aria-hidden="true"
                tabIndex={-1}
              />
            </HStack>
          </Flex>
        </Stack>
      </Container>

      <AnimatePresence>
        {easterEgg && (
          <MotionBox
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={9999}
            bg="black"
            display="flex"
            alignItems="center"
            justifyContent="center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              as="video"
              ref={videoRef}
              src={t.footer.easterEggVideo}
              onCanPlay={handleVideoReady}
              onEnded={() => setEasterEgg(false)}
              autoPlay
              playsInline
              w="100%"
              h="100%"
              objectFit="contain"
            />
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  );
}
